'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { SheetClose } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

export interface NavItem {
  href: string
  label: string
}

// "Prilagodi sebi" je primarna ulazna tačka i stoji prva — vidljiva svima, i
// prijavljenima i gostima, jer wizard radi bez naloga.
export const NAV_ITEMS: readonly NavItem[] = [
  { href: '/prilagodjavanje', label: 'Prilagodi sebi' },
  { href: '/kursevi', label: 'Kursevi' },
  { href: '/o-nama', label: 'O nama' },
  { href: '/pomoc', label: 'Pomoć' },
  { href: '/kontakt', label: 'Kontakt' },
]

const EDUKATOR_ITEM: NavItem = { href: '/edukator', label: 'Edukator panel' }

// Header koristi istu klasu i za stavke van mape (nalog, odjava, prijava),
// pa je izvezena odavde umjesto da se prepisuje u dva fajla.
export const navLinkClass =
  'hover:text-primary focus-visible:ring-ring rounded-md px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'

export const mobileNavLinkClass =
  'hover:bg-accent focus-visible:ring-ring block rounded-md px-3 py-3 text-base font-medium focus-visible:outline-none focus-visible:ring-2'

// Podvlaka i deblji font — oznaka aktivne stavke ne smije zavisiti samo od
// boje (WCAG 1.4.1 Use of Color).
const desktopActiveClass = 'text-primary font-semibold underline decoration-2 underline-offset-8'

// Lijeva ivica na mobilnom; pl-2 (8px) + 4px ivice = px-3 (12px), pa tekst
// ostaje poravnat s neaktivnim stavkama.
const mobileActiveClass = 'text-primary bg-accent border-primary border-l-4 pl-2 font-semibold'

/**
 * Ruta je aktivna i kad smo u njenom podstablu (npr. /kursevi/uvod aktivira
 * "Kursevi"). Root "/" nije u navigaciji, pa nema slučaja gdje bi ovo
 * označilo sve stavke odjednom.
 */
export function isActivePath(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`)
}

interface NavLinksProps {
  /** `desktop` vraća <li> stavke za <ul> u Headeru, `mobile` linkove koji zatvaraju Sheet. */
  variant: 'desktop' | 'mobile'
  isEdukator: boolean
}

export function NavLinks({ variant, isEdukator }: NavLinksProps) {
  const pathname = usePathname()
  const items = isEdukator ? [...NAV_ITEMS, EDUKATOR_ITEM] : NAV_ITEMS

  return (
    <>
      {items.map((item) => {
        const isActive = isActivePath(pathname, item.href)
        const ariaCurrent = isActive ? 'page' : undefined

        if (variant === 'mobile') {
          return (
            <SheetClose asChild key={item.href}>
              <Link
                href={item.href}
                aria-current={ariaCurrent}
                className={cn(mobileNavLinkClass, isActive && mobileActiveClass)}
              >
                {item.label}
              </Link>
            </SheetClose>
          )
        }

        return (
          <li key={item.href}>
            <Link
              href={item.href}
              aria-current={ariaCurrent}
              className={cn(navLinkClass, isActive && desktopActiveClass)}
            >
              {item.label}
            </Link>
          </li>
        )
      })}
    </>
  )
}
